import React, { useEffect, useState, useRef, useCallback } from 'react';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { toast } from 'sonner';
import { MessageSquare, Send, User, Bot, CheckCircle, Clock, Building2, ChevronDown } from 'lucide-react';

const SuperAdminLiveChat = () => {
  const [conversations, setConversations] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selectedConv, setSelectedConv] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const messagesEndRef = useRef(null);
  const pollRef = useRef(null);

  const fetchConversations = useCallback(async () => {
    try {
      const res = await api.superadminGetConversations();
      setConversations(res.data || []);
    } catch (err) { console.warn('Polling conversations failed:', err?.message); }
    finally { setLoading(false); }
  }, []);

  const fetchAgents = useCallback(async () => {
    try {
      const res = await api.superadminGetSupportAgents();
      const active = (res.data || []).filter(a => a.is_active);
      setAgents(active);
      if (active.length && !selectedAgent) setSelectedAgent(active[0]);
    } catch (err) { console.error(err); }
  }, [selectedAgent]);

  const fetchMessages = useCallback(async (convId) => {
    try {
      const res = await api.superadminGetConversationMessages(convId);
      setMessages(res.data || []);
      setConversations(prev => prev.map(c => c.id === convId ? { ...c, unread_count: 0 } : c));
    } catch (err) { toast.error('Kunde inte hämta meddelanden'); }
  }, []);

  useEffect(() => {
    fetchConversations();
    fetchAgents();
    pollRef.current = setInterval(fetchConversations, 5000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchConversations, fetchAgents]);

  useEffect(() => {
    if (selectedConv) fetchMessages(selectedConv.id);
  }, [selectedConv?.id, fetchMessages]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!input.trim() || !selectedConv || !selectedAgent) return;
    setSending(true);
    try {
      await api.superadminReplyToConversation(selectedConv.id, {
        message: input,
        agent_id: selectedAgent.id,
        agent_name: selectedAgent.name,
      });
      setInput('');
      fetchMessages(selectedConv.id);
      fetchConversations();
    } catch (err) { toast.error('Kunde inte skicka meddelande'); }
    finally { setSending(false); }
  };

  const handleResolve = async () => {
    if (!selectedConv) return;
    try {
      await api.superadminResolveConversation(selectedConv.id);
      toast.success('Konversation markerad som löst');
      fetchConversations();
      setSelectedConv(null);
      setMessages([]);
    } catch (err) { toast.error('Kunde inte lösa konversation'); }
  };

  const openConvs = conversations.filter(c => c.status !== 'resolved');
  const resolvedConvs = conversations.filter(c => c.status === 'resolved');

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-6 h-6 border-2 border-[#E8613A] border-t-transparent op-round rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="flex gap-0 h-[calc(100vh-8rem)] -m-6" data-testid="sa-live-chat-page">
      {/* Conversation list */}
      <div className="w-80 flex-shrink-0 bg-card border-r border-border flex flex-col">
        <div className="p-4 border-b border-border">
          <h3 className="font-bold text-sm text-foreground">Konversationer</h3>
          <p className="text-xs text-muted-foreground mt-0.5">{openConvs.length} öppna</p>
        </div>

        {/* Agent selector */}
        {agents.length > 0 && (
          <div className="px-3 py-2 border-b border-border">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Svara som:</p>
            <div className="flex items-center gap-2">
              {selectedAgent?.avatar_url ? (
                <img src={selectedAgent.avatar_url} alt="" className="w-6 h-6 rounded-full object-cover" />
              ) : (
                <div className="w-6 h-6 rounded-full bg-[#E8613A]/10 flex items-center justify-center text-[10px] font-bold text-[#E8613A]">
                  {selectedAgent?.name?.charAt(0) || '?'}
                </div>
              )}
              <select value={selectedAgent?.id || ''} onChange={e => setSelectedAgent(agents.find(a => a.id === e.target.value))}
                className="flex-1 h-8 text-xs rounded-md border border-input bg-background px-2" data-testid="sa-chat-agent-select">
                {agents.map(a => <option key={a.id} value={a.id}>{a.name} — {a.title}</option>)}
              </select>
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {openConvs.length === 0 && (
            <div className="p-6 text-center text-muted-foreground">
              <MessageSquare size={28} className="mx-auto mb-2 opacity-30" />
              <p className="text-xs">Inga aktiva konversationer</p>
            </div>
          )}
          {openConvs.map(conv => (
            <div key={conv.id} onClick={() => setSelectedConv(conv)}
              data-testid={`sa-conv-${conv.id}`}
              className={`p-3 border-b border-border cursor-pointer transition-colors hover:bg-muted/50 ${
                selectedConv?.id === conv.id ? 'bg-muted/70 border-l-2 border-l-[#E8613A]' : ''
              }`}>
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium text-foreground truncate">{conv.user_name || 'Användare'}</p>
                {conv.unread_count > 0 && (
                  <span className="bg-[#E8613A] text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full min-w-[18px] text-center">
                    {conv.unread_count}
                  </span>
                )}
              </div>
              <p className="text-xs text-muted-foreground truncate mt-0.5">
                {conv.last_message?.message?.substring(0, 60) || 'Inget meddelande'}
              </p>
              <div className="flex items-center gap-2 mt-1">
                <span className="text-[10px] text-muted-foreground"><Building2 size={10} className="inline mr-0.5" />{conv.company_name || '—'}</span>
                {conv.assigned_agent && <span className="text-[10px] text-[#E8613A]">{conv.assigned_agent}</span>}
              </div>
            </div>
          ))}

          {resolvedConvs.length > 0 && (
            <details className="border-t border-border">
              <summary className="p-3 text-xs font-medium text-muted-foreground cursor-pointer hover:bg-muted/30 flex items-center gap-1">
                <ChevronDown size={12} /> Lösta ({resolvedConvs.length})
              </summary>
              {resolvedConvs.map(conv => (
                <div key={conv.id} onClick={() => setSelectedConv(conv)}
                  className="p-3 border-b border-border cursor-pointer hover:bg-muted/30 opacity-60">
                  <p className="text-sm font-medium text-foreground truncate">{conv.user_name || 'Användare'}</p>
                  <p className="text-xs text-muted-foreground truncate">{conv.last_message?.message?.substring(0, 40)}</p>
                </div>
              ))}
            </details>
          )}
        </div>
      </div>

      {/* Chat area */}
      <div className="flex-1 flex flex-col min-w-0">
        {!selectedConv ? (
          <div className="flex-1 flex items-center justify-center text-muted-foreground">
            <div className="text-center">
              <MessageSquare size={40} className="mx-auto mb-3 opacity-20" />
              <p className="text-sm">Välj en konversation</p>
            </div>
          </div>
        ) : (
          <>
            {/* Chat header */}
            <div className="px-5 py-3 border-b border-border bg-card flex items-center justify-between flex-shrink-0">
              <div>
                <p className="font-bold text-sm text-foreground">{selectedConv.user_name || 'Användare'}</p>
                <p className="text-xs text-muted-foreground">{selectedConv.company_name || '—'} | {selectedConv.status}</p>
              </div>
              {selectedConv.status !== 'resolved' && (
                <Button size="sm" variant="outline" onClick={handleResolve} className="text-xs h-8" data-testid="sa-resolve-conv">
                  <CheckCircle size={14} className="mr-1" /> Lös
                </Button>
              )}
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-5 space-y-3" data-testid="sa-chat-messages">
              {messages.map(msg => (
                <div key={msg.id} className={`flex gap-2 ${msg.sender_type === 'user' ? '' : 'flex-row-reverse'}`}>
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    msg.sender_type === 'user' ? 'bg-muted text-muted-foreground' :
                    msg.sender_type === 'ai' ? 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-400' :
                    'bg-[#E8613A]/10 text-[#E8613A]'
                  }`}>
                    {msg.sender_type === 'user' ? <User size={14} /> : msg.sender_type === 'ai' ? <Bot size={14} /> : (msg.sender_name?.charAt(0) || 'S')}
                  </div>
                  <div className={`max-w-[70%] ${msg.sender_type === 'user' ? '' : 'text-right'}`}>
                    <p className="text-[10px] text-muted-foreground mb-0.5">
                      {msg.sender_type === 'user' ? (msg.sender_name || 'Kund') : msg.sender_type === 'ai' ? 'AI Bot' : msg.sender_name}
                      <span className="ml-2">{msg.created_at ? new Date(msg.created_at).toLocaleTimeString('sv-SE', { hour: '2-digit', minute: '2-digit' }) : ''}</span>
                    </p>
                    <div className={`px-3 py-2 rounded-lg text-sm inline-block text-left ${
                      msg.sender_type === 'user' ? 'bg-muted text-foreground' :
                      msg.sender_type === 'ai' ? 'bg-purple-50 text-foreground border border-purple-200 dark:bg-purple-900/20 dark:border-purple-800' :
                      'bg-[#E8613A] text-white'
                    }`}>
                      <p className="whitespace-pre-wrap">{msg.message}</p>
                    </div>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>

            {/* Input */}
            {selectedConv.status !== 'resolved' && (
              <div className="p-4 border-t border-border bg-card flex-shrink-0">
                {!selectedAgent ? (
                  <p className="text-xs text-amber-600 text-center">Skapa en kundtjänstmedarbetare under "Kundtjänst" för att svara.</p>
                ) : (
                  <form onSubmit={handleSend} className="flex gap-2">
                    <Input value={input} onChange={e => setInput(e.target.value)} placeholder={`Svara som ${selectedAgent.name}...`}
                      disabled={sending} className="flex-1 h-10 text-sm" data-testid="sa-chat-input" />
                    <Button type="submit" disabled={sending || !input.trim()} className="bg-[#E8613A] hover:bg-[#D4532F] text-white h-10 px-4" data-testid="sa-chat-send">
                      <Send size={16} />
                    </Button>
                  </form>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default SuperAdminLiveChat;
